import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { SidebarProps, SidebarItem } from './types';
import { activeNavPath, itemOwnsPath } from './active-path';

/** Window a single click waits out before committing, when the same item also
 *  has a double-click action bound. Matches the common OS double-click speed. */
const DOUBLE_CLICK_MS = 250;

// ─── Pure helpers (exported for testing) ──────────────────────────

/**
 * Filter sidebar items by user role level and active feature flags.
 * Items whose requiredRole exceeds the user's level or whose featureFlag
 * is disabled are removed from the DOM entirely (not hidden via CSS).
 */
export function filterSidebarItems(
  items: SidebarItem[],
  userRoleLevel: number,
  featureFlags: Record<string, boolean>,
): SidebarItem[] {
  return items
    .filter((item) => {
      if (item.requiredRole !== undefined && userRoleLevel < item.requiredRole) {
        return false;
      }
      if (item.featureFlag && !featureFlags[item.featureFlag]) {
        return false;
      }
      return true;
    })
    .map((item) => {
      if (item.children) {
        return {
          ...item,
          children: filterSidebarItems(item.children, userRoleLevel, featureFlags),
        };
      }
      return item;
    });
}

/**
 * Check if any descendant of an item owns the active path — the resolved one
 * from activeNavPath, not the raw URL.
 * Used to highlight parent items when a child route is active.
 */
export function isChildActive(item: SidebarItem, activePath?: string): boolean {
  if (!item.children || !activePath) return false;
  return item.children.some(
    (child) => itemOwnsPath(child, activePath) || isChildActive(child, activePath),
  );
}

/** Ids of the items whose sub-menu holds the active path, outermost first. */
function activeParentIds(items: SidebarItem[], activePath?: string): string[] {
  const ids: string[] = [];
  for (const item of items) {
    if (item.children && isChildActive(item, activePath)) {
      ids.push(item.id, ...activeParentIds(item.children, activePath));
    }
  }
  return ids;
}

/** Entry in the flat focusable list used by keyboard navigation. */
interface FlatItem {
  item: SidebarItem;
  depth: number;
  parentId?: string;
}

/**
 * Flatten visible sidebar items into an ordered list for keyboard navigation.
 * Only includes items whose parent sub-menu is expanded (tracked by expandedIds).
 * In collapsed mode, children are never visible.
 */
export function flattenVisibleItems(
  items: SidebarItem[],
  expandedIds: Set<string>,
  collapsed: boolean,
  depth = 0,
  parentId?: string,
): FlatItem[] {
  const result: FlatItem[] = [];
  for (const item of items) {
    result.push({ item, depth, parentId });
    if (
      !collapsed &&
      item.children &&
      item.children.length > 0 &&
      expandedIds.has(item.id)
    ) {
      result.push(
        ...flattenVisibleItems(item.children, expandedIds, collapsed, depth + 1, item.id),
      );
    }
  }
  return result;
}

// ─── SidebarItemComponent ─────────────────────────────────────────

interface SidebarItemComponentProps {
  item: SidebarItem;
  collapsed: boolean;
  /** The resolved active path (activeNavPath), not the raw URL. */
  activeItem?: string;
  onNavigate?: (path: string) => void;
  expanded: boolean;
  onToggleExpand: (id: string) => void;
  focusedId?: string;
  itemRef: (el: HTMLDivElement | null) => void;
  childActive: boolean;
  /** When set, double-clicking this item toggles the sidebar instead of navigating. */
  onDoubleActivate?: () => void;
}

function SidebarItemComponent({
  item,
  collapsed,
  activeItem,
  onNavigate,
  expanded,
  onToggleExpand,
  focusedId,
  itemRef,
  childActive,
  onDoubleActivate,
}: SidebarItemComponentProps) {
  const isActive = itemOwnsPath(item, activeItem);
  const hasChildren = item.children && item.children.length > 0;
  const isFocused = focusedId === item.id;

  const activate = useCallback(() => {
    if (hasChildren) {
      onToggleExpand(item.id);
    } else {
      onNavigate?.(item.path);
    }
  }, [hasChildren, onNavigate, onToggleExpand, item.id, item.path]);

  // When the item also carries a double-click action, the single-click
  // navigation has to wait out the double-click window — otherwise every
  // double-click would navigate first and then toggle.
  const clickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
  }, []);

  const handleClick = useCallback(() => {
    if (!onDoubleActivate) {
      activate();
      return;
    }
    if (clickTimerRef.current) clearTimeout(clickTimerRef.current);
    clickTimerRef.current = setTimeout(() => {
      clickTimerRef.current = null;
      activate();
    }, DOUBLE_CLICK_MS);
  }, [activate, onDoubleActivate]);

  const handleDoubleClick = useCallback(() => {
    if (!onDoubleActivate) return;
    if (clickTimerRef.current) {
      clearTimeout(clickTimerRef.current);
      clickTimerRef.current = null;
    }
    onDoubleActivate();
  }, [onDoubleActivate]);

  // Determine styling
  const isActiveOrChildActive = isActive || childActive;
  const itemClasses = [
    'group relative flex cursor-pointer items-center gap-3',
    'rounded-md px-3 py-2 text-sm transition-colors duration-150',
    collapsed ? 'justify-center' : '',
  ];

  if (isActive) {
    itemClasses.push(
      'border-l-[3px] border-brand-500 bg-brand-50 font-medium text-brand-700',
      'dark:bg-brand-900/20 dark:text-brand-300',
    );
  } else if (childActive) {
    // Parent with active child: brand-50 bg, no left border, brand-600 text
    itemClasses.push(
      'border-l-[3px] border-transparent bg-brand-50 font-medium text-brand-600',
      'dark:bg-brand-900/10 dark:text-brand-400',
    );
  } else if (item.locked) {
    // Not in the plan: muted, but still a link — the route explains the lock.
    itemClasses.push(
      'border-l-[3px] border-transparent text-neutral-400',
      'hover:bg-brand-50 dark:text-neutral-500 dark:hover:bg-neutral-800',
    );
  } else {
    itemClasses.push(
      'border-l-[3px] border-transparent text-neutral-600',
      'hover:bg-brand-50 dark:text-neutral-300 dark:hover:bg-neutral-800',
    );
  }

  const lockIcon = (
    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
  );

  return (
    <>
      <div
        ref={itemRef}
        role="treeitem"
        tabIndex={isFocused ? 0 : -1}
        onClick={handleClick}
        onDoubleClick={onDoubleActivate ? handleDoubleClick : undefined}
        aria-expanded={hasChildren ? expanded : undefined}
        aria-current={isActive ? 'page' : undefined}
        aria-selected={isActiveOrChildActive}
        data-sidebar-item-id={item.id}
        title={collapsed ? item.label : undefined}
        className={itemClasses.join(' ')}
      >
        {/* Icon with badge dot in collapsed mode */}
        <span
          className={[
            'relative flex h-5 w-5 flex-shrink-0 items-center justify-center text-base',
            isActive || childActive
              ? 'text-brand-500'
              : 'text-neutral-400 group-hover:text-brand-500',
          ].join(' ')}
          aria-hidden="true"
        >
          {item.icon}
          {/* Collapsed badge dot */}
          {collapsed && item.badge !== undefined && (
            <span
              className={[
                'absolute -right-1 -top-1 h-2 w-2 rounded-full',
                'bg-danger-500',
              ].join(' ')}
              aria-hidden="true"
            />
          )}
          {/* Collapsed lock mark */}
          {collapsed && item.locked && (
            <span
              className="absolute -bottom-1 -right-1 rounded-full bg-white text-warning-600 dark:bg-neutral-900"
              data-sidebar-lock="true"
            >
              {lockIcon}
            </span>
          )}
        </span>

        {/* Label — hidden when collapsed */}
        {!collapsed && (
          <span className="flex-1 truncate">{item.label}</span>
        )}

        {/* Lock (expanded mode) */}
        {!collapsed && item.locked && (
          <span
            role="img"
            aria-label="Not in your plan"
            title="Not in your plan"
            className="flex-shrink-0 text-warning-500 dark:text-warning-400"
            data-sidebar-lock="true"
          >
            {lockIcon}
          </span>
        )}

        {/* Badge (expanded mode) */}
        {!collapsed && item.badge !== undefined && (
          <span
            className={[
              'flex h-5 min-w-5 items-center justify-center rounded-full px-1',
              'text-[10px] font-bold',
              'bg-danger-100 text-danger-700 dark:bg-danger-900/30 dark:text-danger-400',
            ].join(' ')}
          >
            {item.badge}
          </span>
        )}

        {/* Expand/collapse chevron for sub-menus */}
        {!collapsed && hasChildren && (
          <span
            className={[
              'text-xs text-neutral-400 transition-transform duration-150',
              expanded ? 'rotate-90' : '',
            ].join(' ')}
            aria-hidden="true"
          >
            ▸
          </span>
        )}

        {/* Tooltip on collapsed mode */}
        {collapsed && (
          <span
            className={[
              'pointer-events-none absolute left-full ml-2 whitespace-nowrap',
              'rounded bg-neutral-800 px-2 py-1 text-xs text-white',
              'opacity-0 shadow-md transition-opacity duration-150',
              'group-hover:opacity-100',
              'dark:bg-neutral-700',
            ].join(' ')}
            role="tooltip"
          >
            {item.label}
          </span>
        )}
      </div>

      {/* Animated sub-items */}
      {!collapsed && hasChildren && (
        <div
          className={[
            'grid transition-[grid-template-rows] duration-200 ease-out',
            expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
          ].join(' ')}
        >
          <div className="overflow-hidden">
            <ul className="ml-6 mt-0.5 space-y-0.5" role="group">
              {item.children!.map((child) => (
                <li key={child.id}>
                  <SidebarItemComponent
                    item={child}
                    collapsed={false}
                    activeItem={activeItem}
                    onNavigate={onNavigate}
                    expanded={false}
                    onToggleExpand={onToggleExpand}
                    focusedId={focusedId}
                    itemRef={() => {}}
                    childActive={isChildActive(child, activeItem)}
                  />
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </>
  );
}

// ─── Scroll shadow hook ───────────────────────────────────────────

function useScrollShadow(ref: React.RefObject<HTMLDivElement | null>) {
  const [showTopShadow, setShowTopShadow] = useState(false);
  const [showBottomShadow, setShowBottomShadow] = useState(false);

  const updateShadows = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    setShowTopShadow(el.scrollTop > 0);
    setShowBottomShadow(
      el.scrollTop + el.clientHeight < el.scrollHeight - 1,
    );
  }, [ref]);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    updateShadows();
    el.addEventListener('scroll', updateShadows, { passive: true });
    const observer = new ResizeObserver(updateShadows);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateShadows);
      observer.disconnect();
    };
  }, [ref, updateShadows]);

  return { showTopShadow, showBottomShadow };
}

// ─── Main Sidebar component ──────────────────────────────────────

export function Sidebar({
  items,
  collapsed,
  onToggle,
  activeItem,
  tenant,
  user,
  featureFlags = {},
  onNavigate,
  overlay = false,
  onOverlayClose,
  triggerRef,
  doubleClickToToggle,
  expandedGroups: expandedGroupsProp,
  onExpandedGroupsChange,
}: SidebarProps) {
  const sidebarRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const previousFocusRef = useRef<Element | null>(null);

  const userRoleLevel = user?.roleLevel ?? 0;
  const filteredItems = filterSidebarItems(items, userRoleLevel, featureFlags);
  // The nav path the current URL belongs to (/bills/123 → /bills). Every
  // highlight below compares against this, never against the raw URL.
  const activeMatch = activeNavPath(filteredItems, activeItem);

  // Track which sub-menus are expanded — parents of the active item start open
  const [expandedIds, setExpandedIds] = useState<Set<string>>(
    () => new Set(activeParentIds(filteredItems, activeMatch)),
  );

  const toggleExpand = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  // Expanded section groups — sections start collapsed. Controlled through
  // props when the host persists them, otherwise tracked here. Only meaningful
  // while the sidebar is expanded: icon-only mode has no headers to reopen a
  // group, so it always shows every item.
  const [localExpandedGroups, setLocalExpandedGroups] = useState<string[]>([]);
  const expandedGroupList = expandedGroupsProp ?? localExpandedGroups;
  const expandedGroups = useMemo(() => new Set(expandedGroupList), [expandedGroupList]);

  const toggleGroup = useCallback(
    (group: string) => {
      const next = expandedGroups.has(group)
        ? expandedGroupList.filter((g) => g !== group)
        : [...expandedGroupList, group];
      if (expandedGroupsProp === undefined) setLocalExpandedGroups(next);
      onExpandedGroupsChange?.(next);
    },
    [expandedGroups, expandedGroupList, expandedGroupsProp, onExpandedGroupsChange],
  );

  // Reveal the active item whenever the route lands on a new one, not only on
  // mount: open its parent sub-menus and its section. Runs once per active
  // path, so the user can still close them again while on the same page.
  const activeParentKey = activeParentIds(filteredItems, activeMatch).join('\n');
  const activeGroup = filteredItems.find(
    (item) => itemOwnsPath(item, activeMatch) || isChildActive(item, activeMatch),
  )?.group;
  const revealedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (!activeMatch || revealedRef.current === activeMatch) return;
    revealedRef.current = activeMatch;
    if (activeParentKey) {
      const parents = activeParentKey.split('\n');
      setExpandedIds((prev) =>
        parents.every((id) => prev.has(id)) ? prev : new Set([...prev, ...parents]),
      );
    }
    if (activeGroup && !expandedGroupList.includes(activeGroup)) {
      const next = [...expandedGroupList, activeGroup];
      if (expandedGroupsProp === undefined) setLocalExpandedGroups(next);
      onExpandedGroupsChange?.(next);
    }
  }, [
    activeMatch,
    activeParentKey,
    activeGroup,
    expandedGroupList,
    expandedGroupsProp,
    onExpandedGroupsChange,
  ]);

  // Flat visible items for keyboard navigation — items inside a collapsed
  // section are hidden, so they are skipped too.
  const navigableItems = useMemo(
    () =>
      collapsed
        ? filteredItems
        : filteredItems.filter((item) => !item.group || expandedGroups.has(item.group)),
    [filteredItems, collapsed, expandedGroups],
  );
  const flatItems = useMemo(
    () => flattenVisibleItems(navigableItems, expandedIds, collapsed),
    [navigableItems, expandedIds, collapsed],
  );

  // Roving tabindex — track focused item
  const [focusedId, setFocusedId] = useState<string | undefined>(
    flatItems[0]?.item.id,
  );

  // If the focused item disappears (its section or sub-menu was collapsed),
  // hand the roving tabindex back to the first visible item.
  useEffect(() => {
    if (focusedId && !flatItems.some((f) => f.item.id === focusedId)) {
      setFocusedId(flatItems[0]?.item.id);
    }
  }, [flatItems, focusedId]);

  const focusItem = useCallback((id: string) => {
    setFocusedId(id);
    const el = itemRefs.current.get(id);
    el?.focus();
  }, []);

  // Scroll shadows
  const { showTopShadow, showBottomShadow } = useScrollShadow(scrollRef);

  // ─── Keyboard navigation ──────────────────────────────────────

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      // Section headers are plain buttons inside the scroll area; their own
      // Enter/Space must not be re-read as tree navigation.
      if (!(e.target as HTMLElement).closest('[role="treeitem"]')) return;
      const currentIndex = flatItems.findIndex((f) => f.item.id === focusedId);
      if (currentIndex === -1) return;

      const current = flatItems[currentIndex];

      switch (e.key) {
        case 'ArrowDown': {
          e.preventDefault();
          if (currentIndex < flatItems.length - 1) {
            focusItem(flatItems[currentIndex + 1].item.id);
          }
          break;
        }
        case 'ArrowUp': {
          e.preventDefault();
          if (currentIndex > 0) {
            focusItem(flatItems[currentIndex - 1].item.id);
          }
          break;
        }
        case 'ArrowRight': {
          e.preventDefault();
          const hasChildren = current.item.children && current.item.children.length > 0;
          if (!hasChildren || collapsed) break;
          if (!expandedIds.has(current.item.id)) {
            // Expand sub-menu
            toggleExpand(current.item.id);
          } else {
            // Move to first child
            const firstChild = current.item.children![0];
            if (firstChild) focusItem(firstChild.id);
          }
          break;
        }
        case 'ArrowLeft': {
          e.preventDefault();
          if (current.parentId) {
            // Move focus to parent
            focusItem(current.parentId);
          } else if (
            current.item.children &&
            current.item.children.length > 0 &&
            expandedIds.has(current.item.id)
          ) {
            // Collapse sub-menu
            toggleExpand(current.item.id);
          }
          break;
        }
        case 'Home': {
          e.preventDefault();
          if (flatItems.length > 0) focusItem(flatItems[0].item.id);
          break;
        }
        case 'End': {
          e.preventDefault();
          if (flatItems.length > 0) focusItem(flatItems[flatItems.length - 1].item.id);
          break;
        }
        case 'Enter':
        case ' ': {
          e.preventDefault();
          const hasChildItems = current.item.children && current.item.children.length > 0;
          if (hasChildItems) {
            toggleExpand(current.item.id);
          } else {
            onNavigate?.(current.item.path);
          }
          break;
        }
      }
    },
    [flatItems, focusedId, focusItem, expandedIds, toggleExpand, collapsed, onNavigate],
  );

  // ─── Focus trap for overlay mode ──────────────────────────────

  useEffect(() => {
    if (!overlay) return;

    // Save current focus and move to first sidebar item
    previousFocusRef.current = document.activeElement;
    if (flatItems.length > 0) {
      // Slight delay to ensure DOM is ready
      requestAnimationFrame(() => {
        focusItem(flatItems[0].item.id);
      });
    }

    function handleGlobalKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onOverlayClose?.();
        return;
      }

      // Tab trap
      if (e.key === 'Tab') {
        const sidebar = sidebarRef.current;
        if (!sidebar) return;

        const focusable = sidebar.querySelectorAll<HTMLElement>(
          '[tabindex]:not([tabindex="-1"]), button, a[href], input',
        );
        if (focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    }

    document.addEventListener('keydown', handleGlobalKeyDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
      // Restore focus on close
      const returnTarget = triggerRef?.current ?? previousFocusRef.current;
      if (returnTarget && returnTarget instanceof HTMLElement) {
        returnTarget.focus();
      }
    };
  }, [overlay, onOverlayClose, triggerRef, flatItems, focusItem]);

  // ─── Ref callback for items ───────────────────────────────────

  const makeItemRef = useCallback(
    (id: string) => (el: HTMLDivElement | null) => {
      if (el) {
        itemRefs.current.set(id, el);
      } else {
        itemRefs.current.delete(id);
      }
    },
    [],
  );

  // ─── Section group rendering ──────────────────────────────────
  // Consecutive items sharing `group` form a section: a divider, then a header
  // that expands the section's items (sections start collapsed). Ungrouped
  // items render flat.

  const sections: { group?: string; items: SidebarItem[] }[] = [];
  for (const item of filteredItems) {
    const last = sections[sections.length - 1];
    if (last && last.group === item.group) last.items.push(item);
    else sections.push({ group: item.group, items: [item] });
  }

  const divider = collapsed ? (
    <div className="mx-auto my-1 w-6 border-t border-neutral-200 dark:border-neutral-700" />
  ) : (
    <div
      className="mx-2 my-2 border-t border-neutral-200 dark:border-neutral-700"
      role="separator"
    />
  );

  const renderItem = (item: SidebarItem) => (
    <li key={item.id} role="none">
      {item.dividerBefore && divider}
      <SidebarItemComponent
        item={item}
        collapsed={collapsed}
        activeItem={activeMatch}
        onNavigate={onNavigate}
        expanded={expandedIds.has(item.id)}
        onToggleExpand={toggleExpand}
        focusedId={focusedId}
        itemRef={makeItemRef(item.id)}
        childActive={isChildActive(item, activeMatch)}
        onDoubleActivate={
          doubleClickToToggle && !item.children?.length ? onToggle : undefined
        }
      />
    </li>
  );

  function renderSections() {
    return sections.flatMap((section, index) => {
      if (section.group === undefined) return section.items.map(renderItem);
      const group = section.group;
      const open = collapsed || expandedGroups.has(group);
      const containsActive = section.items.some(
        (item) => itemOwnsPath(item, activeMatch) || isChildActive(item, activeMatch),
      );

      return (
        <li key={`group:${group}`} role="none">
          {index > 0 && divider}

          {/* Section header — toggles the section; tinted when it hides the active item */}
          {!collapsed && (
            <button
              type="button"
              onClick={() => toggleGroup(group)}
              aria-expanded={open}
              className={[
                'flex w-full items-center justify-between rounded-md px-3 pb-1 pt-4',
                'text-[11px] font-medium uppercase tracking-wider',
                'transition-colors duration-150 hover:text-brand-600 dark:hover:text-brand-400',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500',
                containsActive && !open
                  ? 'text-brand-600 dark:text-brand-400'
                  : 'text-neutral-400 dark:text-neutral-500',
              ].join(' ')}
            >
              <span>{group}</span>
              <span
                className={[
                  'text-xs transition-transform duration-150',
                  open ? 'rotate-90' : '',
                ].join(' ')}
                aria-hidden="true"
              >
                ▸
              </span>
            </button>
          )}

          {/* Animated section body — same grid-rows technique as sub-menus */}
          <div
            className={[
              'grid transition-[grid-template-rows] duration-200 ease-out',
              open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            ].join(' ')}
          >
            <div className="overflow-hidden">
              <ul className="space-y-0.5" role="group">
                {section.items.map(renderItem)}
              </ul>
            </div>
          </div>
        </li>
      );
    });
  }

  // ─── Scroll shadow styles ─────────────────────────────────────

  const scrollShadowStyle: React.CSSProperties = {};
  if (showTopShadow && showBottomShadow) {
    scrollShadowStyle.boxShadow =
      'inset 0 8px 6px -6px rgb(0 0 0 / 0.08), inset 0 -8px 6px -6px rgb(0 0 0 / 0.08)';
  } else if (showTopShadow) {
    scrollShadowStyle.boxShadow = 'inset 0 8px 6px -6px rgb(0 0 0 / 0.08)';
  } else if (showBottomShadow) {
    scrollShadowStyle.boxShadow = 'inset 0 -8px 6px -6px rgb(0 0 0 / 0.08)';
  }

  // ─── Sidebar content ─────────────────────────────────────────

  const sidebarContent = (
    <nav
      ref={sidebarRef}
      role="navigation"
      aria-label="Main navigation"
      className={[
        'relative flex h-full flex-col overflow-x-hidden bg-white dark:bg-neutral-900',
        'border-r border-neutral-200 dark:border-neutral-700',
        'transition-[width] duration-200 ease-in-out',
        collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      ].join(' ')}
    >
      {/* Tenant branding */}
      <div
        className={[
          'flex h-header items-center border-b border-neutral-100',
          'dark:border-neutral-800',
          collapsed ? 'justify-center px-2' : 'gap-3 px-4',
        ].join(' ')}
      >
        {tenant?.logo ? (
          <img
            src={tenant.logo}
            alt={tenant.name}
            className="h-8 w-8 flex-shrink-0 rounded-md object-contain"
          />
        ) : (
          <div
            className={[
              'flex h-8 w-8 flex-shrink-0 items-center justify-center',
              'rounded-md bg-brand-500 text-sm font-bold text-white',
            ].join(' ')}
          >
            {tenant?.name?.charAt(0).toUpperCase() ?? 'B'}
          </div>
        )}
        {!collapsed && tenant && (
          <span className="truncate text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {tenant.name}
          </span>
        )}
      </div>

      {/* Navigation items with scroll shadows */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto overflow-x-hidden px-2 py-3"
        style={scrollShadowStyle}
        onKeyDown={handleKeyDown}
      >
        <ul className="space-y-0.5" role="tree">
          {renderSections()}
        </ul>
      </div>

      {/* User profile at bottom */}
      {user && (
        <div
          className={[
            'border-t border-neutral-100 px-3 py-3',
            'dark:border-neutral-800',
            collapsed ? 'flex justify-center' : 'flex items-center gap-3',
          ].join(' ')}
        >
          <div
            className={[
              'flex h-8 w-8 flex-shrink-0 items-center justify-center',
              'rounded-full bg-brand-100 text-xs font-semibold text-brand-700',
              'dark:bg-brand-900 dark:text-brand-300',
            ].join(' ')}
          >
            {user.avatar ? (
              <img
                src={user.avatar}
                alt={user.name}
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              user.name.charAt(0).toUpperCase()
            )}
          </div>
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium text-neutral-900 dark:text-neutral-100">
                {user.name}
              </p>
              <p className="truncate text-xs text-neutral-500 dark:text-neutral-400">
                {user.role}
              </p>
            </div>
          )}
        </div>
      )}


    </nav>
  );

  // ─── Overlay mode for tablet ──────────────────────────────────

  if (overlay) {
    return (
      <>
        {/* Backdrop */}
        <div
          className={[
            'fixed inset-0 z-modal-backdrop bg-neutral-900/50',
            'animate-fade-in',
          ].join(' ')}
          onClick={onOverlayClose}
          aria-hidden="true"
        />
        {/* Sidebar panel — slides in from left */}
        <div
          className={[
            'fixed inset-y-0 left-0 z-modal',
            'animate-slide-in-left',
          ].join(' ')}
        >
          {sidebarContent}
        </div>
      </>
    );
  }

  return sidebarContent;
}
